import React, { useEffect, useMemo, useState } from "react";

import "../css/inventory.css";
import ActivityChart from "../components/common/ActivityChart";
import PageHeader from "../components/inventory/PageHeader";
import CategoryBox from "../components/inventory/CategoryBox";
import InventoryTable from "../components/inventory/InventoryTable";
import FilterBar from "../components/inventory/FilterBar";
import { getInventoriesAPI } from "../services/inventoryService";
import { limitNumber } from "../utils/number";

// const stockData = {
//   labels: ["In Stock (>45)", "Low Stock (1-45)", "Out of Stock (0)"],
//   values: [0, 0, 1],
//   colors: ["#505050", "#B8860B", "#cccccc"],
// };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ name: "", category: "" });
  const [filtered, setFiltered] = useState(items);
  const [loading, setLoading] = useState(false);

  const [total, setTotal] = useState(1);

  useEffect(() => {
    getInventories();
  }, []);

  async function getInventories() {
    try {
      setLoading(true);
      const response = await getInventoriesAPI();

      if (response.success) {
        const products = response.data || [];
        const structured = products?.map((product) => {
          const isCash = product.category === "Cash";

          return {
            id: product._id,
            name: `${product?.itemType} ${product?.brand ? `(${product?.brand})` : ""}`.toUpperCase(),
            category: product.category || "Unknown",
            brand: product.brand,
            weight: `${isCash ? `Rs ${limitNumber(product.totalAmount)}` : `${limitNumber(product.totalWeight)} g`} `,
            qty: `${isCash ? "-" : (product.qty ?? 0)}`,
          };
        });

        setItems(structured);
        setFiltered(structured);
      } else {
        alert("Error occured while fetching the inventories");
      }

      // console.log(response);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!filter.name && !filter.category) {
      setFiltered(items);
      return;
    }
    const filtered = items.filter((i) => {
      const matchedCategory = filter.category
        ? i.category.toLowerCase() === filter.category.toLocaleLowerCase()
        : true;

      const matchedName = filter.name
        ? i.name.toLowerCase().includes(filter.name.toLocaleLowerCase())
        : true;

      return matchedCategory && matchedName;
    });

    setFiltered(filtered);
  }, [filter]);

  const stockData = useMemo(() => {
    let inStock = 0;
    let lowStock = 0;
    let outStock = 0;

    items.forEach((item) => {
      if (item.qty > 45) {
        inStock++;
      } else if (item.qty > 0) {
        lowStock++;
      } else {
        outStock++;
      }
    });

    const total = inStock + lowStock + outStock;
    setTotal(total);
    return {
      labels: ["In Stock (>45)", "Low Stock (1-45)", "Out of Stock (0)"],
      values: [inStock, lowStock, outStock],
      colors: ["#505050", "#B8860B", "#cccccc"],
    };
  }, [items]);

  const getPercent = (val) => (total ? Math.round((val / total) * 100) : 0);

  return (
    <>
      <PageHeader />
      {/* Inventory Page Content */}
      <div className="inventory-container px-4 py-4">
        {/* Category Boxes */}
        <CategoryBox setCategoryFilter={setFilter} filters={filter} />
        {/* Main Content Row */}
        <div className="row">
          {/* Left Content (Table Area) */}
          <div className="col-lg-8">
            {/* Filter Bar */}
            <FilterBar filters={filter} setCategoryFilter={setFilter} />
            {/* Table */}
            <InventoryTable products={filtered || []} loading={loading} />
          </div>
          {/* Right Sidebar */}
          <div className="col-lg-4">
            {/* Stock Status */}
            <div className="stock-status-box">
              <h5>Stock Status</h5>
              <div className="stock-chart">
                <ActivityChart data={stockData} hideLabels={true} />
              </div>
              <div className="stock-legend">
                <div className="legend-item">
                  <span className="legend-dot in-stock" />
                  <span className="legend-label">In Stock</span>
                  <span className="legend-value">
                    {getPercent(stockData?.values?.[0])}%
                  </span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot low-stock" />
                  <span className="legend-label">Low Stock</span>
                  <span className="legend-value">
                    {getPercent(stockData?.values?.[1])}%
                  </span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot out-stock" />
                  <span className="legend-label">Out of Stock</span>
                  <span className="legend-value">
                    {getPercent(stockData?.values?.[2])}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
